import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Navigate, Link } from 'react-router-dom';
import {
  Home as HomeIcon, Store, Factory, Wrench, ShieldCheck, Clock, CheckCircle2,
  Phone, ArrowRight, ClipboardCheck, PackageCheck, Search, RefreshCw, ChevronDown,
  Gauge, Snowflake, Cpu, CircleDot, Award, MapPin, FileText, Zap
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { SEO } from '../components/SEO';
import { QuickLeadForm } from '../components/QuickLeadForm';
import { MachineQuoteForm } from '../components/MachineQuoteForm';
import { RebuildNotice } from '../components/RebuildNotice';
import { ROICalculator } from '../components/ROICalculator';
import { CaseStudies } from '../components/CaseStudies';
import { ReviewForm } from '../components/ReviewForm';
import { LocalReviews } from '../components/LocalReviews';
import { ManualDownloadLink } from '../components/ManualDownloadLink';
import { SpecsSection } from '../components/SpecsSection';
import { TrustBadges } from '../components/TrustBadges';
import { TurnaroundBadge, TurnaroundNote } from '../components/TurnaroundBadge';
import { Testimonials } from '../components/Testimonials';
import { CONTACT } from '../lyphline';
import { WorkshopGallery, IllustratedCapabilities } from '../components/WorkshopGallery';
import { PHOTO } from '../workshopPhotos';

const IMG = {
  tech: PHOTO.controlHousing,
  pump: PHOTO.serviceValve,
  parts: PHOTO.soldering,
  inspect: PHOTO.boardTest,
  bench: PHOTO.rewiring,
};

const MACHINES = {
  residential: {
    icon: HomeIcon,
    eyebrow: 'Residential Freeze Dryers',
    h1: 'Residential Freeze Dryer Rebuilds',
    lede: "Home freeze dryers work hard, and when one stops mid batch, a season of harvest can be at risk. We rebuild residential units back to dependable, everyday performance.",
    heroImg: PHOTO.nitrogen,
    introImg: PHOTO.controlHousing,
    introImgAlt: 'Rebuilding a residential freeze dryer control housing',
    metaTitle: 'Residential Freeze Dryer Repair & Rebuilds',
    metaDescription: 'Independent freeze dryer repair and rebuilds for residential and home units. Fix a freeze dryer that won\'t pull vacuum, won\'t freeze, throws error codes or leaves product wet. Vacuum pump replacement, 90-day rebuild warranty, free diagnostic quote.',
    keywords: 'freeze dryer repair, residential freeze dryer repair, home freeze dryer rebuild, freeze dryer not pulling vacuum, freeze dryer won\'t freeze, freeze dryer vacuum pump replacement, freeze dryer error codes',
    audience: 'Small & medium home units used by preppers, gardeners and home food storage enthusiasts.',
    intro: "Most home freeze dryers are built for occasional use, but many owners run them almost continuously through harvest season. That duty cycle eventually wears out pumps, seals and refrigeration, and a machine that won't finish a batch can spoil everything inside it. As an independent rebuilder, we restore home scale units of select makes and models so you can get back to preserving without paying for a brand new machine.",
    problems: [
      "Won't pull or hold vacuum",
      "Not freezing or cooling properly",
      "Loud, hot or failing vacuum pump",
      "Product still wet at the end of a cycle",
      "Blank display, error codes or won't power on",
      "Cracked door gasket or a chamber that won't seal",
    ],
    whyTitle: 'Rebuild vs. replace at home',
    why: "A quality rebuild typically costs a fraction of a new unit and returns your machine to reliable performance, often better than when it was new, because we correct the parts that fail first. It's the practical choice for households that depend on their freeze dryer season after season.",
    seoCopy: [
      "Looking for freeze dryer repair for a home or medium-size unit? LyphLine rebuilds residential freeze dryers that won't pull vacuum, won't freeze or cool, throw error codes, or leave product still wet at the end of a cycle. As an independent rebuilder we service most major freeze dryer brands of select makes and models.",
      "Common home freeze dryer fixes include vacuum pump replacement, refrigeration and condenser rebuilds, control board and sensor repair, and door gasket and seal replacement so the chamber holds vacuum again. Every rebuild is bench-tested and backed by our 90-day rebuild warranty. Start with a free diagnostic quote and we'll confirm your machine is a fit before any work begins.",
    ],
  },
  'small-business': {
    icon: Store,
    eyebrow: 'Small & Growing Business Freeze Dryers',
    h1: 'Small Business Freeze Dryer Rebuilds',
    lede: "When your freeze dryer is part of how you earn, downtime costs orders and reputation. We prioritise rebuilds for small and growing food businesses so you get back to filling demand fast.",
    heroImg: PHOTO.brazing,
    introImg: PHOTO.soldering,
    introImgAlt: 'Soldering a freeze dryer control board for a small food business',
    metaTitle: 'Small Business Freeze Dryer Repair & Rebuilds',
    metaDescription: 'Priority freeze dryer repair and rebuilds for small businesses, cafés, farms and candy makers. Fix stalled production, weak vacuum and failed cycles fast. Vacuum pump replacement, emergency shipping, 90-day rebuild warranty.',
    keywords: 'commercial freeze dryer repair, small business freeze dryer rebuild, freeze dryer for candy business, farm freeze dryer repair, freeze dryer vacuum pump replacement, freeze dryer downtime',
    audience: 'Large & extra large units run by cafés, farms, candy makers and growing food producers.',
    intro: "Plenty of successful food businesses start on larger home grade freeze dryers, they're affordable and surprisingly capable. But pushed through commercial duty cycles, they eventually stall, and every day offline is product you can't sell. We treat these machines as the revenue tools they are: fast diagnostics, prioritised rebuilds, and modifications that add the durability higher volumes demand.",
    problems: [
      "Machine down and stalling production",
      "Vacuum or refrigeration can't keep up with volume",
      "Vacuum pump worn out from heavy daily cycles",
      "Inconsistent or incomplete drying across batches",
      "Repeated error codes or failed cycles",
      "Seals, sensors and boards worn from constant use",
    ],
    whyTitle: 'Protect the business, not just the machine',
    why: "For a working business, the real cost of downtime is the orders you can't fill and the growth you put on hold, far bigger than any rebuild bill. A fast, dependable rebuild (and optional modifications) protects the operation you've built around the machine.",
    seoCopy: [
      "Need fast commercial freeze dryer repair for a café, farm, candy maker or growing food business? We prioritise rebuilds for machines that have stalled production, can't keep up with volume, or keep failing cycles. As an independent rebuilder we service most major freeze dryer brands.",
      "Typical business fixes include heavy-duty vacuum pump replacement, refrigeration and condenser rebuilds, control board repair, and durability modifications for constant daily use. With emergency shipping to cut downtime and a 90-day rebuild warranty, you get back to filling orders quickly. Request a free diagnostic quote before any work begins.",
    ],
  },
  'semi-commercial': {
    icon: Factory,
    eyebrow: 'Semi Commercial & Production Freeze Dryers',
    h1: 'Semi Commercial Freeze Dryer Rebuilds',
    lede: "On a production floor, uptime is everything. We rebuild and modify semi commercial freeze dryers with the parts, process and turnaround serious operations need.",
    heroImg: PHOTO.rewiring,
    introImg: PHOTO.boardTest,
    introImgAlt: 'Testing a production freeze dryer control board',
    metaTitle: 'Semi Commercial Freeze Dryer Rebuilds & Modifications',
    metaDescription: 'Production freeze dryer repair, rebuilds and modifications for semi commercial units and lyophilizers used by manufacturers and labs. Refrigeration, vacuum and control system rebuilds, 90-day rebuild warranty, production scheduling with rush options.',
    keywords: 'production freeze dryer repair, semi commercial freeze dryer rebuild, lyophilizer rebuild, freeze dryer modification, industrial freeze dryer repair, freeze dryer refrigeration rebuild',
    audience: 'Semi commercial & production equipment for manufacturers, nutraceutical and research operations.',
    intro: "Production scale freeze dryers are complex, high value assets, and an unplanned outage can halt an entire line. Our technicians know these machines inside and out, rebuilding refrigeration, vacuum and control systems, and modifying weak points so your equipment runs harder for longer. Production scheduling with rush options keeps critical operations moving.",
    problems: [
      "Line stopped by an unplanned outage",
      "Refrigeration or vacuum performance falling off",
      "Failing or aging control systems",
      "Long, inconsistent or aborted production cycles",
      "Worn components approaching end of life",
      "Engineering weak points causing repeat failures",
    ],
    whyTitle: 'Rebuild, modify and future proof',
    why: "A rebuild restores your machine; a modification improves it. We upgrade durability and modern controls to correct the engineering weak points that cause repeat failures, extending service life and protecting the throughput your operation depends on.",
    seoCopy: [
      "Searching for production freeze dryer repair or a lyophilizer rebuild? We rebuild and modify semi-commercial and production freeze dryers with failing refrigeration, vacuum or control systems, and correct the engineering weak points behind repeat failures. As an independent rebuilder we service most major freeze dryer brands.",
      "Services include full vacuum and refrigeration rebuilds, control system upgrades, sensor and board replacement, and freeze dryer modifications that extend service life. Production scheduling with rush options keeps critical lines moving, and every rebuild carries a 90-day rebuild warranty. Start with a free diagnostic quote.",
    ],
  },
};

const PROCESS = [
  { icon: Search, title: 'Free diagnostic quote', desc: "Tell us what's happening. We assess the machine and send a written report and firm quote." },
  { icon: Wrench, title: 'Rebuild or modify', desc: 'Once approved, our technicians restore your machine using quality aftermarket parts.' },
  { icon: ClipboardCheck, title: 'Tested & warrantied', desc: 'Every rebuild is tested and backed by our own 90 day rebuild warranty.' },
  { icon: PackageCheck, title: 'Back online', desc: 'Packed up and returned running, with emergency shipping available across Canada & the USA.' },
];

const INCLUDED = [
  { icon: Gauge, title: 'Vacuum system', desc: 'Pump service or replacement, leak checks and vacuum testing.' },
  { icon: Snowflake, title: 'Refrigeration & condenser', desc: 'Cooling diagnostics, coil and compressor rebuild.' },
  { icon: Cpu, title: 'Control board & sensors', desc: 'Board diagnostics, sensor and display replacement.' },
  { icon: CircleDot, title: 'Seals & gaskets', desc: 'Door gaskets and seals replaced so the chamber holds vacuum.' },
  { icon: ClipboardCheck, title: 'Full cycle testing', desc: 'End-to-end run and calibration before it ships back.' },
  { icon: ShieldCheck, title: '90 day warranty', desc: 'Every rebuild is backed by our own rebuild warranty.' },
];

const STATS = [
  { icon: Award, label: '90-Day', sub: 'Rebuild warranty' },
  { icon: Wrench, label: 'Every', sub: 'Make & model' },
  { icon: FileText, label: 'Free', sub: 'Diagnostic quote' },
  { icon: MapPin, label: 'Serving', sub: 'Canada & the USA' },
];

const ORDER = ['residential', 'small-business', 'semi-commercial'];
const NAMES = { residential: 'Residential', 'small-business': 'Small Business', 'semi-commercial': 'Semi Commercial' };

const SUBNAV = [
  { id: 'overview', label: 'Overview' },
  { id: 'problems', label: 'Problems We Fix' },
  { id: 'included', label: "What's Included" },
  { id: 'process', label: 'How It Works' },
  { id: 'faq', label: 'FAQ' },
];

const FAQItem = ({ q, a, testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-xl bg-white overflow-hidden" data-testid={testid}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left" data-testid={`${testid}-toggle`}>
        <span className="font-semibold text-[#003456]">{q}</span>
        <ChevronDown className={`h-5 w-5 text-[#CE8946] flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <p className="px-5 pb-5 text-slate-600 text-sm leading-relaxed">{a}</p>}
    </div>
  );
};

const buildFaqs = (slug) => ([
  { q: `Do you rebuild ${NAMES[slug].toLowerCase()} freeze dryers of select makes and models?`, a: `Yes. We're an independent rebuilder and service ${slug === 'residential' ? 'home scale' : slug === 'small-business' ? 'large and extra large' : 'semi commercial and production'} freeze dryers of select makes and models. Send us your machine details and we'll confirm whether yours is a fit.` },
  { q: 'Are these OEM (manufacturer) rebuilds?', a: "No. LyphLine is an independent rebuilder. In most cases we restore your machine as a full or partial rebuild using quality aftermarket (non OEM) parts, backed by our own 90 day rebuild warranty. This is not an OEM or manufacturer warranty service." },
  { q: 'How long does a rebuild take?', a: slug === 'residential' ? 'Most home units are turned around in roughly 5 to 10 days once we receive them.' : slug === 'small-business' ? 'We prioritise business machines, with emergency shipping available to minimise your downtime.' : 'Timelines depend on scope; production scheduling with rush options is available to keep critical production moving.' },
  { q: 'What does it cost?', a: 'Every quote starts with a free diagnostic. We assess the machine and send a written report with a firm price before any work begins, no surprises.' },
  { q: 'Do you offer upgrades, not just rebuilds?', a: 'Yes. Modifications future proof your machine with upgraded durability, modern controls and personal changes, correcting the weak points that tend to fail first.' },
  { q: "Why won't my freeze dryer pull vacuum?", a: "A freeze dryer that won't pull or hold vacuum usually points to a worn vacuum pump, a failing door gasket or seal, or a leak in the vacuum lines. We pressure test the system, replace the pump or seals as needed and confirm it holds vacuum before it ships back." },
  { q: "My freeze dryer isn't freezing or getting cold, can you fix it?", a: "Yes. Poor cooling is typically a refrigeration issue, such as a low charge, a failing compressor or a blocked condenser. We diagnose and rebuild the refrigeration system so the machine reaches and holds temperature again." },
  { q: 'What do freeze dryer error codes or a blank display mean?', a: "Error codes, a frozen screen or a unit that won't power on usually trace back to the control board, wiring or a failed sensor. We test the electronics, replace the faulty parts and recalibrate the machine." },
  { q: 'My product is still wet at the end of a cycle, why?', a: "Incomplete drying is often caused by weak vacuum, refrigeration that can't hold temperature, or worn sensors reporting the wrong readings. We find the root cause and rebuild the affected system so cycles finish properly." },
  { q: 'Do you service most freeze dryer brands?', a: "We're an independent rebuilder and service most major freeze dryer brands of select makes and models. Send us your machine details and we'll confirm whether yours is a fit." },
]);

const CtaBand = ({ heading, sub, tid }) => (
  <section className="section-padding bg-white" data-testid={tid}>
    <div className="max-w-5xl mx-auto bg-gradient-to-r from-[#003456] to-[#023F63] rounded-2xl px-6 sm:px-10 py-8 flex flex-col sm:flex-row sm:items-center gap-5">
      <div className="flex-1">
        <h3 className="text-xl sm:text-2xl font-bold text-white">{heading}</h3>
        <p className="text-white/65 text-sm mt-1.5">{sub}</p>
      </div>
      <a href="#quote" className="inline-flex items-center justify-center gap-2 bg-[#CE8946] hover:bg-[#B87333] text-[#003456] font-bold px-7 py-3.5 rounded-md transition-colors whitespace-nowrap" data-testid={`${tid}-btn`}>
        <Wrench className="h-5 w-5" /> Get my free quote
      </a>
    </div>
  </section>
);

const MachineLanding = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const prefill = {
    symptom: searchParams.get('symptom') || '',
    size: searchParams.get('size') || '',
    urgency: searchParams.get('urgency') || '',
    email: searchParams.get('email') || '',
  };
  const prefilled = searchParams.get('prefilled') === '1';
  const m = MACHINES[slug];

  useEffect(() => {
    if (prefilled) {
      const t = setTimeout(() => {
        const el = document.getElementById('quote');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 450);
      return () => clearTimeout(t);
    }
  }, [prefilled, slug]);

  if (!m) return <Navigate to="/machines/residential" replace />;
  const Icon = m.icon;
  const faqs = buildFaqs(slug);
  const others = ORDER.filter((s) => s !== slug);

  return (
    <div className="min-h-screen bg-white">
      <SEO title={m.metaTitle} description={m.metaDescription} keywords={m.keywords} faq={faqs} />
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#003456]" data-testid="machine-hero">
        <img src={m.heroImg} alt={`${m.h1} service`} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#003456] via-[#003456]/92 to-[#003456]/55" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-32 pb-16 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 bg-[#CE8946]/15 text-[#CE8946] px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.2em] border border-[#CE8946]/30">
              <Icon className="h-4 w-4" /> {m.eyebrow}
            </span>
            <h1 className="text-4xl sm:text-5xl font-extrabold text-white mt-6 leading-[1.1]">{m.h1}</h1>
            <p className="text-lg text-white/75 mt-5 max-w-xl leading-relaxed">{m.lede}</p>
            <TurnaroundBadge className="mt-6" />
            <div className="flex flex-wrap gap-4 mt-5">
              <a href="#quote" className="inline-flex items-center gap-2 bg-[#CE8946] hover:bg-[#B87333] text-[#003456] font-bold px-7 py-4 rounded-md transition-colors shadow-xl shadow-[#CE8946]/20" data-testid="machine-hero-quote-btn">
                <Wrench className="h-5 w-5" /> Get a Free Quote
              </a>
              <a href={CONTACT.phoneHref} className="inline-flex items-center gap-2 border border-white/25 text-white font-semibold px-7 py-4 rounded-md hover:bg-white/10 transition-colors">
                <Phone className="h-5 w-5 text-[#CE8946]" /> {CONTACT.phone}
              </a>
              <ManualDownloadLink testid="machine-manual" />
              <ManualDownloadLink
                match="quick guide"
                label="Quick start guide"
                Icon={Zap}
                testid="machine-quickguide"
                className="inline-flex items-center gap-2 border border-white/25 text-white font-semibold px-7 py-4 rounded-md hover:bg-white/10 transition-colors"
              />
            </div>
            <TurnaroundNote className="text-white/55 mt-3" />
            <div className="flex flex-wrap gap-6 mt-6 text-sm text-white/60">
              <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#CE8946]" /> 90 day rebuild warranty</span>
              <span className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[#CE8946]" /> Select makes &amp; models</span>
              <span className="flex items-center gap-2"><Clock className="h-4 w-4 text-[#CE8946]" /> Fast turnaround</span>
            </div>
          </div>
          {/* Inline quote form */}
          <div id="quote" className="bg-white border border-slate-200 rounded-2xl p-7 shadow-2xl w-full max-w-md lg:ml-auto scroll-mt-28" data-testid="machine-quote-card">
            <h3 className="text-xl font-bold text-[#003456]">Free diagnostic quote</h3>
            <p className="text-slate-500 text-sm mt-1 mb-5">A few quick questions, takes under a minute.</p>
            <MachineQuoteForm slug={slug} tierLabel={`${NAMES[slug]} freeze dryer`} testid="machine-lead-form" prefill={prefill} />
            <TrustBadges variant="light" className="mt-5 pt-5 border-t border-slate-100" />
          </div>
        </div>
      </section>

      {/* Sticky in-page navigation */}
      <nav className="sticky top-20 z-40 bg-white/95 backdrop-blur border-b border-slate-200" data-testid="machine-subnav">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto no-scrollbar">
            {SUBNAV.map((s) => (
              <a key={s.id} href={`#${s.id}`} className="whitespace-nowrap text-sm font-medium text-slate-600 hover:text-[#003456] px-3 sm:px-4 py-4 border-b-2 border-transparent hover:border-[#CE8946] transition-colors" data-testid={`machine-subnav-${s.id}`}>
                {s.label}
              </a>
            ))}
            <a href="#quote" className="ml-auto whitespace-nowrap inline-flex items-center gap-1.5 text-sm font-bold text-[#CE8946] hover:text-[#B87333] px-3 sm:px-4 py-2 my-2 rounded-md transition-colors" data-testid="machine-subnav-quote">
              Free quote <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </nav>

      {/* Overview (text + image) */}
      <section id="overview" className="py-16 section-padding bg-white scroll-mt-32" data-testid="machine-intro">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div>
            <span className="text-[#B87333] font-semibold text-xs uppercase tracking-[0.2em]">Who this is for</span>
            <p className="text-[#003456] font-semibold text-lg mt-3">{m.audience}</p>
            <p className="text-slate-600 mt-4 leading-relaxed">{m.intro}</p>
            <a href="#quote" className="inline-flex items-center gap-2 mt-6 text-[#003456] font-bold hover:text-[#CE8946] transition-colors" data-testid="machine-intro-cta">
              Start a free diagnostic <ArrowRight className="h-4 w-4" />
            </a>
          </div>
          <div className="relative">
            <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-xl aspect-[4/3]">
              <img src={m.introImg} alt={m.introImgAlt} className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-5 -left-3 sm:left-6 bg-[#003456] text-white rounded-xl px-5 py-3 shadow-lg flex items-center gap-3">
              <ShieldCheck className="h-6 w-6 text-[#CE8946]" />
              <div className="leading-tight">
                <p className="font-bold text-sm">90 day rebuild warranty</p>
                <p className="text-white/60 text-xs">on every machine we rebuild</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problems we fix */}
      <section id="problems" className="py-16 section-padding bg-[#F1F5F9] scroll-mt-32" data-testid="machine-problems">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-10">
            <span className="text-[#B87333] font-semibold text-xs uppercase tracking-[0.2em]">Common problems we fix</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#003456] mt-3">Sound familiar?</h2>
            <p className="text-slate-600 mt-3">If your machine is showing any of these, we can help get it back online.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {m.problems.map((p, i) => (
              <div key={i} className="flex items-start gap-3 bg-white border border-slate-200 rounded-xl p-5" data-testid={`machine-problem-${i}`}>
                <CheckCircle2 className="h-5 w-5 text-[#CE8946] flex-shrink-0 mt-0.5" />
                <span className="text-[#003456] font-medium text-sm leading-snug">{p}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CtaBand tid="machine-cta-1" heading="Not sure exactly what's wrong?" sub="That's fine, start a free diagnostic and our techs will pinpoint it for you." />

      {/* What's included (image + checklist) */}
      <section id="included" className="py-20 section-padding bg-white scroll-mt-32" data-testid="machine-included">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-xl aspect-[4/3] order-last lg:order-first">
            <img src={IMG.pump} alt="Vacuum and refrigeration components rebuilt by LyphLine" className="w-full h-full object-cover" />
          </div>
          <div>
            <span className="text-[#B87333] font-semibold text-xs uppercase tracking-[0.2em]">What every rebuild covers</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#003456] mt-3">A complete, tested rebuild</h2>
            <p className="text-slate-600 mt-3">We work through the systems that fail first, then test the whole machine before it goes back to you.</p>
            <div className="grid sm:grid-cols-2 gap-5 mt-8">
              {INCLUDED.map((it, i) => (
                <div key={i} className="flex items-start gap-3" data-testid={`machine-included-${i}`}>
                  <span className="w-10 h-10 rounded-lg bg-[#CE8946]/15 flex items-center justify-center flex-shrink-0"><it.icon className="h-5 w-5 text-[#B87333]" /></span>
                  <div>
                    <h3 className="text-[#003456] font-bold text-sm">{it.title}</h3>
                    <p className="text-slate-500 text-sm mt-0.5 leading-snug">{it.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats / trust band */}
      <section className="py-12 section-padding bg-[#023F63]" data-testid="machine-stats">
        <div className="max-w-7xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-6">
          {STATS.map((s, i) => (
            <div key={i} className="flex items-center gap-4" data-testid={`machine-stat-${i}`}>
              <span className="w-12 h-12 rounded-xl bg-[#CE8946]/15 flex items-center justify-center flex-shrink-0"><s.icon className="h-6 w-6 text-[#CE8946]" /></span>
              <div className="leading-tight">
                <p className="text-2xl font-extrabold text-white">{s.label}</p>
                <p className="text-white/60 text-sm">{s.sub}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Process */}
      <section id="process" className="py-20 section-padding bg-[#003456] scroll-mt-32" data-testid="machine-process">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#CE8946] font-semibold text-xs uppercase tracking-[0.2em]">How it works</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mt-3">A simple path back to running</h2>
              <p className="text-white/70 mt-4 leading-relaxed">From first diagnostic to a tested, warrantied machine, here's how we get you back online.</p>
              <div className="mt-8 space-y-5">
                {PROCESS.map((p, i) => (
                  <div key={i} className="flex items-start gap-4" data-testid={`machine-process-${i}`}>
                    <span className="w-11 h-11 rounded-xl bg-[#CE8946]/15 flex items-center justify-center flex-shrink-0"><p.icon className="h-5 w-5 text-[#CE8946]" /></span>
                    <div>
                      <h3 className="text-white font-bold">{p.title}</h3>
                      <p className="text-white/60 text-sm mt-1 leading-relaxed">{p.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden border border-white/10">
              <img src={IMG.tech} alt="Technician rebuilding a freeze dryer" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Illustrated capabilities */}
      <IllustratedCapabilities bg="bg-[#F1F5F9]" />

      {/* Gallery strip */}
      <WorkshopGallery
        bg="bg-white"
        eyebrow="Inside the rebuild"
        title="Careful, hands on work"
        subtitle="Every machine is diagnosed, rebuilt and bench-tested by hand in our shop before it ships back to you."
        testid="machine-gallery"
      />

      <CtaBand tid="machine-cta-2" heading="Ready to get your machine back online?" sub="A quick, no obligation quote is the fastest first step. It takes under a minute." />

      {/* Electrical & environmental specifications */}
      <SpecsSection bg="bg-[#F1F5F9]" />

      {/* Rebuild ROI */}
      <ROICalculator />
      <CaseStudies />

      {/* Testimonials */}
      <Testimonials bg="bg-white" />

      {/* Why rebuild */}
      <section className="py-16 section-padding bg-[#F1F5F9]" data-testid="machine-why">
        <div className="max-w-4xl mx-auto bg-white border border-[#CE8946]/30 rounded-2xl p-8 sm:p-10">
          <span className="w-14 h-14 rounded-xl bg-[#CE8946]/15 flex items-center justify-center mb-5"><RefreshCw className="h-7 w-7 text-[#CE8946]" /></span>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#003456]">{m.whyTitle}</h2>
          <p className="text-slate-600 mt-4 leading-relaxed">{m.why}</p>
          <a href="#quote" className="inline-flex items-center gap-2 mt-6 bg-[#CE8946] hover:bg-[#B87333] text-[#003456] font-bold px-6 py-3 rounded-md transition-colors" data-testid="machine-why-cta">
            Start my free quote <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </section>

      {/* Rebuild disclosure */}
      <section className="section-padding bg-[#F1F5F9] pb-8">
        <div className="max-w-4xl mx-auto"><RebuildNotice /></div>
      </section>

      {/* Review capture */}
      {/* Approved reviews for this machine tier */}
      <LocalReviews
        subject={`machine:${slug}`}
        heading={`What ${NAMES[slug]} freeze dryer owners say`}
        blurb={`Real feedback from ${NAMES[slug].toLowerCase()} freeze dryer owners whose machines we've rebuilt.`}
        testid="machine-local-reviews"
      />

      <ReviewForm subject={`machine:${slug}`} testid="machine-review-form" />

      {/* SEO copy — freeze dryer repair & rebuild services */}
      <section className="py-16 section-padding bg-[#F1F5F9]" data-testid="machine-seo-copy">
        <div className="max-w-3xl mx-auto">
          <span className="text-[#B87333] font-semibold text-xs uppercase tracking-[0.2em]">Freeze dryer repair &amp; rebuild services</span>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#003456] mt-3 mb-5">{m.h1} — what we fix and how</h2>
          <div className="space-y-4">
            {m.seoCopy.map((para, i) => (
              <p key={i} className="text-slate-600 leading-relaxed" data-testid={`machine-seo-para-${i}`}>{para}</p>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-16 section-padding bg-white scroll-mt-32" data-testid="machine-faq">
        <div className="max-w-3xl mx-auto">
          <div className="mb-8">
            <span className="text-[#B87333] font-semibold text-xs uppercase tracking-[0.2em]">FAQ</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#003456] mt-3">Questions, answered</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((f, i) => <FAQItem key={i} q={f.q} a={f.a} testid={`machine-faq-${i}`} />)}
          </div>
        </div>
      </section>

      {/* Other tiers */}
      <section className="py-16 section-padding bg-[#003456]" data-testid="machine-cross-links">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <span className="text-[#CE8946] font-semibold text-xs uppercase tracking-[0.2em]">Not the right fit?</span>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mt-3">We rebuild machines of varying sizes</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            {others.map((s) => {
              const OIcon = MACHINES[s].icon;
              return (
                <Link key={s} to={`/machines/${s}`} className="group bg-[#023F63] border border-white/10 rounded-2xl p-8 hover:border-[#CE8946]/50 transition-colors flex items-center justify-between gap-4" data-testid={`machine-link-${s}`}>
                  <div className="flex items-center gap-4">
                    <span className="w-12 h-12 rounded-xl bg-[#CE8946]/15 flex items-center justify-center"><OIcon className="h-6 w-6 text-[#CE8946]" /></span>
                    <div>
                      <h3 className="text-lg font-bold text-white">{NAMES[s]} Machines</h3>
                      <p className="text-white/60 text-sm mt-0.5">{MACHINES[s].eyebrow}</p>
                    </div>
                  </div>
                  <ArrowRight className="h-5 w-5 text-[#CE8946] group-hover:translate-x-1 transition-transform" />
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MachineLanding;
